import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { eliminarFotosDeProducto } from './fotosService';
import { registrarEvento } from './analyticsService';

// Capa de datos del módulo de SERVICIOS. Sigue el mismo patrón que
// productosService/categoriasService: consulta por negocio_id (multi-tenant)
// y deja que RLS haga cumplir el aislamiento en la base.

// ---- Servicios --------------------------------------------------------------

// Catálogo PÚBLICO de servicios (para clientes, sin cuenta). Solo servicios
// visibles y activos. RLS anon lo permite si el catálogo del negocio es público.
export async function obtenerServiciosPublicos(negocioId) {
  const { data, error } = await supabase
    .from('servicios')
    .select('*')
    .eq('negocio_id', negocioId)
    .eq('visible_catalogo', true)
    .eq('activo', true)
    .order('destacado', { ascending: false })
    .order('nombre', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Datos públicos del negocio (marca) por su nombre corto (slug).
export async function obtenerNegocioPublicoPorSlug(slug) {
  const { data, error } = await supabase
    .from('negocios_publicos')
    .select('id, nombre, logo_url, color_principal, telefono, instagram, moneda')
    .eq('slug', slug)
    .eq('catalogo_publico', true)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function obtenerServicios() {
  const { data, error } = await supabase
    .from('servicios')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('orden', { ascending: true })
    .order('creado_en', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Siguiente código correlativo por categoría de servicio (ej. VJE-0001).
// El prefijo viene de la categoría (tipo 'servicio'). Si el servicio no tiene
// categoría, se devuelve null (el código es opcional en servicios).
export async function generarCodigoServicio(nombreCategoria) {
  if (!nombreCategoria) return null;
  const { data: cat, error: errCat } = await supabase
    .from('categorias')
    .select('prefijo_codigo')
    .eq('negocio_id', getNegocioId())
    .eq('nombre', nombreCategoria)
    .eq('tipo', 'servicio')
    .maybeSingle();
  if (errCat) throw errCat;
  if (!cat || !cat.prefijo_codigo) return null;
  const prefijo = cat.prefijo_codigo;
  const { data, error } = await supabase
    .from('servicios')
    .select('codigo')
    .eq('negocio_id', getNegocioId())
    .like('codigo', prefijo + '-%')
    .order('codigo', { ascending: false })
    .limit(1);
  if (error) throw error;
  let siguiente = 1;
  if (data && data.length > 0 && data[0].codigo) {
    const ultimo = parseInt(data[0].codigo.split('-')[1], 10);
    if (!Number.isNaN(ultimo)) siguiente = ultimo + 1;
  }
  return prefijo + '-' + String(siguiente).padStart(4, '0');
}

export async function crearServicio(datos) {
  const codigo = datos.codigo || (await generarCodigoServicio(datos.categoria));
  const { data: creado, error } = await supabase
    .from('servicios')
    .insert([{ ...datos, negocio_id: getNegocioId(), codigo }])
    .select()
    .single();
  if (error) throw error;
  registrarEvento('servicio_creado', { categoria: creado.categoria || null });
  return creado;
}

export async function actualizarServicio(id, datos) {
  const { error } = await supabase
    .from('servicios')
    .update({ ...datos, actualizado_en: new Date().toISOString() })
    .eq('id', id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
}

// Elimina el servicio y limpia sus fotos de Storage (best-effort, igual que
// productos: si la limpieza falla, el servicio ya quedó eliminado).
export async function eliminarServicio(servicio) {
  const { error } = await supabase
    .from('servicios')
    .delete()
    .eq('id', servicio.id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
  try {
    await eliminarFotosDeProducto(servicio); // mismo formato foto_1_url..foto_5_url
    return { fotosLimpiadas: true };
  } catch {
    return { fotosLimpiadas: false };
  }
}

export async function marcarFotoPrincipalServicio(id, numeroFoto) {
  const { error } = await supabase
    .from('servicios')
    .update({ foto_principal: numeroFoto })
    .eq('id', id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
}

// ---- Categorías de servicio (reusa la tabla categorias con tipo 'servicio') --

const aCategoriaApp = (r) => ({
  id: r.id,
  nombre: r.nombre,
  icono: r.icono || '🧰',
  color: r.color_fondo || '#EEF2FF',
  borde: r.color || '#818CF8',
  prefijo: r.prefijo_codigo,
  orden: r.orden,
});

export async function obtenerCategoriasServicio() {
  const { data, error } = await supabase
    .from('categorias')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .eq('tipo', 'servicio')
    .order('orden', { ascending: true });
  if (error) throw error;
  return (data || []).map(aCategoriaApp);
}

export async function crearCategoriaServicio({ nombre, prefijo, icono, color, borde, orden = 0 }) {
  const { data, error } = await supabase
    .from('categorias')
    .insert([{
      negocio_id: getNegocioId(),
      nombre: nombre.trim(),
      prefijo_codigo: (prefijo || '').trim().toUpperCase() || null,
      icono: icono || '🧰',
      color: borde || '#818CF8',
      color_fondo: color || '#EEF2FF',
      tipo: 'servicio',
      orden,
    }])
    .select()
    .single();
  if (error) throw error;
  return aCategoriaApp(data);
}

// ---- Frases rápidas (biblioteca reutilizable del negocio) -------------------
// tipo: 'incluye' | 'no_incluye' | 'requisito'

export async function obtenerFrases() {
  const { data, error } = await supabase
    .from('servicio_frases')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('tipo', { ascending: true })
    .order('orden', { ascending: true })
    .order('creado_en', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Devuelve las frases agrupadas por tipo: { incluye:[], no_incluye:[], requisito:[] }.
export async function obtenerFrasesAgrupadas() {
  const filas = await obtenerFrases();
  const grupos = { incluye: [], no_incluye: [], requisito: [] };
  filas.forEach((f) => { if (grupos[f.tipo]) grupos[f.tipo].push(f); });
  return grupos;
}

export async function crearFrase({ tipo, texto }) {
  const t = String(texto || '').trim();
  if (!t) throw Object.assign(new Error('Escribe la frase'), { code: 'DATO_INVALIDO' });
  const { data, error } = await supabase
    .from('servicio_frases')
    .insert([{ negocio_id: getNegocioId(), tipo, texto: t }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function eliminarFrase(id) {
  const { error } = await supabase
    .from('servicio_frases')
    .delete()
    .eq('id', id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
}

// ---- Adicionales frecuentes (biblioteca reutilizable del negocio) -----------

export async function obtenerAdicionalesPredef() {
  const { data, error } = await supabase
    .from('servicio_adicionales_predef')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .order('orden', { ascending: true })
    .order('creado_en', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function crearAdicionalPredef({ nombre, precio, impuesto_pct }) {
  const n = String(nombre || '').trim();
  if (!n) throw Object.assign(new Error('Escribe el nombre del adicional'), { code: 'DATO_INVALIDO' });
  const { data, error } = await supabase
    .from('servicio_adicionales_predef')
    .insert([{ negocio_id: getNegocioId(), nombre: n, precio: Number(precio) || 0, impuesto_pct: Number(impuesto_pct) || 0 }])
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function eliminarAdicionalPredef(id) {
  const { error } = await supabase
    .from('servicio_adicionales_predef')
    .delete()
    .eq('id', id)
    .eq('negocio_id', getNegocioId());
  if (error) throw error;
}

// ---- Adicionales / add-ons de un servicio -----------------------------------

export async function obtenerAdicionales(servicioId) {
  const { data, error } = await supabase
    .from('servicio_adicionales')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .eq('servicio_id', servicioId)
    .order('orden', { ascending: true });
  if (error) throw error;
  return data || [];
}

// Reemplaza la lista de adicionales de un servicio (borra los previos e inserta
// los nuevos). Simple y suficiente para el catálogo; la edición fina puede
// venir después. `lista` = [{ nombre, precio, impuesto_pct }].
export async function reemplazarAdicionales(servicioId, lista) {
  const negocioId = getNegocioId();
  const { error: errDel } = await supabase
    .from('servicio_adicionales')
    .delete()
    .eq('negocio_id', negocioId)
    .eq('servicio_id', servicioId);
  if (errDel) throw errDel;
  const filas = (lista || [])
    .filter((a) => a && String(a.nombre || '').trim())
    .map((a, i) => ({
      negocio_id: negocioId,
      servicio_id: servicioId,
      nombre: String(a.nombre).trim(),
      precio: Number(a.precio) || 0,
      impuesto_pct: Number(a.impuesto_pct) || 0,
      orden: i,
    }));
  if (filas.length === 0) return [];
  const { data, error } = await supabase.from('servicio_adicionales').insert(filas).select();
  if (error) throw error;
  return data || [];
}
